// src/oura/merchant/drawers/WorkDrawer.tsx
//
// One line of this month's plan: what it is, when it happens, and what the
// latest scan measured on the checks it covers. Opened from a row of This
// month on Work; a line waiting on an open question opens that question instead.

import { CheckIcon } from "lucide-react";
import type { ClientPagePayload, ClientPlanItem } from "@service/aeo/clientReview";
import { Drawer, DrawerBlock, DrawerHead, MERCHANT_STATUS, Tag, dataSel, returnFocusChain } from "@oura/ui";
import { lineDescription, lineWhen, planLineTitle } from "../model";

export function WorkDrawer({
    item,
    engagement,
    onClose,
}: {
    /** The line on show; null keeps the drawer shut. */
    item: ClientPlanItem | null;
    engagement: ClientPagePayload["engagement"];
    onClose: () => void;
}) {
    if (!item) return null;
    const what = lineDescription(item, engagement);
    const when = lineWhen(item, engagement);
    return (
        <Drawer open onClose={onClose} returnFocus={returnFocusChain(dataSel("plan-code", item.code), `[data-group-head="this-month.${item.status}"]`)}>
            <DrawerHead title={planLineTitle(item)} status={{ tone: MERCHANT_STATUS[item.status].tone, label: MERCHANT_STATUS[item.status].label }} />
            {item.line && <DrawerBlock label="Where it stands">{item.line}{item.month ? ` Part of month ${item.month}.` : ""}</DrawerBlock>}
            {what && <DrawerBlock label="What it is">{what}</DrawerBlock>}
            {item.fixed && item.fixed.length > 0 && (
                <DrawerBlock label="What we fixed">
                    <ul className="m-0 list-plain space-y-1 p-0">
                        {item.fixed.map((f) => (
                            <li key={f} className="flex items-center gap-2">
                                <CheckIcon aria-hidden size={14} strokeWidth={2.5} className="shrink-0 text-emerald-500" />
                                <span>{f}</span>
                            </li>
                        ))}
                    </ul>
                </DrawerBlock>
            )}
            {when && <DrawerBlock label="When">{when}</DrawerBlock>}
            {(item.reportedByHand || measuredKeys(item).length > 0) && (
                <DrawerBlock label="What we measure">
                    <MeasuredChecks item={item} />
                </DrawerBlock>
            )}
        </Drawer>
    );
}

/**
 * What the measurement says about a line, today.
 *
 * `measured[key] === null` is a REAL ANSWER: the check has never been measured
 * for this business, and it must not look like a measured zero. "We have not
 * looked" and "we looked and nothing is wrong" are different facts, so they
 * get different words and different colours.
 */
export function MeasuredChecks({ item }: { item: ClientPlanItem }) {
    if (item.reportedByHand) {
        // `reportedByHand` and the `reported_by_hand` STATUS are not the same
        // thing, and only the status leaves the count (staging 3fdc24e6). A line
        // that is hand-reported AND done is counted; telling it otherwise would
        // contradict the "Done" above it.
        return (
            <p className="m-0 text-gray-600">
                {item.status === "reported_by_hand"
                    ? "We account for this one by hand, so it is not part of the count."
                    : "We account for this one by hand."}
            </p>
        );
    }
    return (
        <div className="mt-1 flex flex-wrap gap-1.5">
            {measuredKeys(item).map((k) => {
                const m = item.measured[k];
                if (m === null) {
                    // The check key is all the server sends for a check it has
                    // never read; its words are the best label there is.
                    return <Tag key={k} tone="gray">{k.replace(/_/g, " ")} · not measured yet</Tag>;
                }
                return m.state === "clear" ? (
                    <Tag key={k} tone="green">{m.label} · clear</Tag>
                ) : (
                    <Tag key={k} tone="rose">
                        {m.label} · {m.count} {m.count === 1 ? "page" : "pages"}
                    </Tag>
                );
            })}
        </div>
    );
}

/**
 * What the measurement says, as one or two plain sentences, for the board's
 * cards (16 Oct 2026): "2 of 4 checks are clear. Still to fix: duplicate
 * title tags on 2 pages and images missing alt text on 41 pages." A check
 * never measured is said as such, never counted as clear. Null when the line
 * covers no check the server read.
 */
export function measuredSentence(item: ClientPlanItem): string | null {
    if (item.reportedByHand) {
        return item.status === "reported_by_hand"
            ? "We account for this one by hand, so it is not part of the count."
            : "We account for this one by hand.";
    }
    const keys = measuredKeys(item);
    if (keys.length === 0) return null;
    const open: string[] = [];
    let clear = 0;
    let never = 0;
    for (const k of keys) {
        const m = item.measured[k];
        if (m === null) never += 1;
        else if (m.state === "clear") clear += 1;
        else open.push(`${m.label.charAt(0).toLowerCase()}${m.label.slice(1)} on ${m.count} ${m.count === 1 ? "page" : "pages"}`);
    }
    const list = open.length > 1 ? `${open.slice(0, -1).join(", ")} and ${open[open.length - 1]}` : open[0];
    return [
        `${clear} of ${keys.length} ${keys.length === 1 ? "check is" : "checks are"} clear.`,
        open.length > 0 && `Still to fix: ${list}.`,
        never > 0 && `${never} not measured yet.`,
    ]
        .filter(Boolean)
        .join(" ");
}

/** The checks this line covers that the server sent a reading (or a stated "never measured") for. */
export function measuredKeys(item: ClientPlanItem): string[] {
    return item.covers.filter((k) => k in item.measured);
}
